import './Game.css';
import React, { useEffect, useState } from 'react';
import { DndContext, useSensor, PointerSensor, useSensors } from '@dnd-kit/core';
import { FlatIndexToSpatialIndices, IndicesFromKey, ApplyRotation } from './utils.js';
import Unit from './Unit.jsx';
import GameGrid from './GameGrid.jsx';
import { GridSquare } from './GridSquare.js';
import miners from './miners.json';

function Game({ game, SetGame, SetMiningRate }) {
  const { m, n } = game;
  const [mapObjects, setMapObjects] = useState(game.objects);
  const [freeUnits, setFreeUnits] = useState(game.units);
  const [miningRate, setMiningRate] = useState(0);

  // Game loop
  useEffect(() => {
    if (mapObjects === undefined) { return; }

    let currentMiningRate = 0;

    const units = Object.entries(mapObjects).filter(([_, value]) => value['type'] === 'unit');

    const multipliers = units.map(([key, value]) => {
      const supportRanges = miners[value['name']]['supportRanges']

      if (supportRanges === undefined) {
        return null;
      };

      const [x, y] = IndicesFromKey(key);

      return Object.entries(supportRanges).map(([supportRange, multiplier]) => {
        const [dx, dy] = ApplyRotation(IndicesFromKey(supportRange), value['rotation']);
        return [`(${x + dx},${y + dy})`, multiplier];
      });
    }).filter(v => v !== null).flat().reduce((accumulator, [tile, multiplier]) => {
      if (accumulator[tile] === undefined) {
        return { ...accumulator, [tile]: multiplier }
      }
      return { ...accumulator, [tile]: accumulator[tile] * multiplier }
    }, {});

    const newMapObjects = mapObjects;
    Object.entries(newMapObjects).forEach(([key, _]) => {
      if (newMapObjects[key]['type'] !== 'resource') {
        return;
      }
      newMapObjects[key]['left'] = newMapObjects[key]['available'];
    });

    units.forEach(([key, value]) => {
      const [x, y] = IndicesFromKey(key);
      for (const [rangeKey, rangeRate] of Object.entries(miners[value['name']]['range'])) {
        const [dx, dy] = ApplyRotation(IndicesFromKey(rangeKey), value['rotation']);
        const targetKey = `(${x + dx},${y + dy})`
        const target = newMapObjects[targetKey];
        if (target === undefined) {
          continue;
        }
        if (target['type'] !== 'resource') {
          continue;
        }
        if (target['left'] === 0) {
          continue;
        }

        let multiplier = multipliers[key];
        if (multiplier === undefined) {
          multiplier = 1;
        }

        const r = rangeRate * multiplier;

        if (target['left'] > r) {
          currentMiningRate += target['multiplier'] * r;
          newMapObjects[targetKey]['left'] -= r;
        } else {
          currentMiningRate += target['multiplier'] * target['left'];
          newMapObjects[targetKey]['left'] = 0;
        }
      }
    });

    setMiningRate(currentMiningRate);
    setMapObjects(newMapObjects);
  }, [mapObjects]);

  useEffect(() => {
    if (SetMiningRate !== undefined) {
      SetMiningRate(miningRate);
    }

  }, [SetMiningRate, miningRate]);

  useEffect(() => {
    if (SetGame !== undefined) {
      SetGame({
        m: m,
        n: n,
        objects: mapObjects,
      });
    }
  }, [SetGame, m, n, mapObjects])

  const returnRotateUnit = (unitId) => {
    return () => {
      for (const [key, value] of Object.entries(mapObjects)) {
        if (value['type'] !== 'unit') {
          continue;
        }
        if (value['name'] !== unitId) {
          continue;
        }
        const { [key]: _, ...newMapObjects } = mapObjects;
        setMapObjects({ ...newMapObjects, [key]: { ...value, 'rotation': (value['rotation'] + 90) % 360 } });
        return;
      }
    };
  }

  return (
    <div className='game-container'>
      <DndContext
        sensors={useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 10 } }))}
        onDragEnd={(e) => {
          // Early return if not dropped on droppable
          if (e.over === null) {
            return;
          }

          // If there is something in the droppable already, don't allow the placement
          const droppedKey = e.over.id.replace("grid-square-", '');
          if (mapObjects[droppedKey] !== undefined) {
            return;
          }

          const droppedId = e.active.id;

          // Remove unit from free units
          setFreeUnits(freeUnits.filter(value => value !== droppedId));

          // Remove unit from original position already in map
          for (const [key, value] of Object.entries(mapObjects)) {
            if (value['type'] !== 'unit') {
              continue;
            }
            if (value['name'] !== droppedId) {
              continue;
            }
            const { [key]: _, ...newMapObjects } = mapObjects;
            setMapObjects({ ...newMapObjects, [droppedKey]: value });
            return;
          }

          setMapObjects({
            ...mapObjects,
            [droppedKey]: {
              'type': 'unit',
              'name': droppedId,
              'rotation': 0,
            }
          });
        }}>
        <GameGrid m={m} n={n}>
          {[...Array(m * n)].map((_, i) => {
            const [y, x] = FlatIndexToSpatialIndices(m, n, i);
            const key = `(${x},${y})`;
            return (
              <GridSquare key={key} id={`grid-square-${key}`} style={{ width: '100%', height: '100%' }}>
                {function () {
                  const object = mapObjects[key];
                  if (object === undefined) {
                    return null;
                  }
                  if (object['type'] === 'resource') {
                    return (<div style={{ position: 'relative', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <p className='prevent-select' style={{ position: 'absolute', top: 0, right: 0, margin: 0, fontSize: '12px' }}>{`x${object['multiplier'].toFixed(1)}`}</p>
                      <p className='prevent-select' style={{ margin: 0 }}>{`${Math.round(object['left'] * 100) / 100}/${object['available']}`}</p>
                    </div>);
                  }
                  if (object['type'] === 'unit') {
                    const unitId = object['name'];
                    return <Unit id={unitId} rotation={object['rotation']} rotateClockwise={returnRotateUnit(unitId)}>{unitId}</Unit>;
                  }
                }()
                }
              </GridSquare>);
          })}
        </GameGrid>
        <div id="free-unit-container">
          {freeUnits.map((value, i) => <Unit key={i} id={value}>{value}</Unit>)}
        </div>
      </DndContext>
      <p style={{marginTop: '10px'}}>Score: {miningRate.toFixed(2)}</p>
    </div>
  );
}

export default Game;